import { useCallback, useMemo } from 'react';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { getLastCampaignsListPage } from './lastCampaignsListPage';

export interface CampaignsListTarget {
  /** Route of the list a campaign sits under (e.g. `/campaigns`, `/brands/$brandId`). */
  to: string;
  params?: Record<string, string>;
  /** Page-memory scope; must match the one the list page records its page under. */
  scope?: string;
}

export interface CampaignsListLink {
  href: string;
  goToList: () => void;
}

/**
 * A campaign's parent in the hierarchy: the campaigns list at the page the user
 * left it on. Both the in-app Back button and the parent crumb use it, so they
 * always go up a level; stepping back through history is the browser's job.
 * The host app owns the route, so it passes the target in.
 */
export function useCampaignsListLink({ to, params, scope }: CampaignsListTarget): CampaignsListLink {
  const router = useRouter();
  const navigate = useNavigate();
  // Callers may build `params` fresh each render, so key on its content.
  const paramsKey = params ? JSON.stringify(params) : '';
  // A builder rather than an object: the remembered page is read each time it's called.
  // `as never`: this router-agnostic package can't reference the app's route tree.
  const buildTarget = useCallback(
    () =>
      ({
        to,
        params: paramsKey ? JSON.parse(paramsKey) : undefined,
        search: { page: getLastCampaignsListPage(scope) },
      }) as never,
    [to, paramsKey, scope],
  );
  // The list page can't change while the user is on a campaign, so the href is built once per target.
  const href = useMemo(() => router.buildLocation(buildTarget()).href, [router, buildTarget]);
  // Reads the page at click time, not render time, so a stale render can't send the user to an old page.
  const goToList = useCallback(() => void navigate(buildTarget()), [navigate, buildTarget]);
  return { href, goToList };
}
